import type { ComponentProps } from "react";
import { Streamdown, type Components, type ExtraProps } from "streamdown";
import type { ApiPlaylistModel } from "../../domains/playlist/hooks/useGetRecommendedPlaylistsQuery";
import { getRoomName } from "../../domains/room/hooks/useRoomName";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import ArticleLabelIcon from "../../assets/icons/article-label-v2.svg?react";
import StarsIcon from "../../assets/icons/stars.svg?react";

// Streamdown styles bold as `<span class="font-semibold">`, which is a lighter
// weight than the `<strong>` this description rendered before and carries none
// of the element's semantics. The description is editor prose, not a chat
// answer, so it keeps the plain tag.
function Strong(props: ComponentProps<"strong"> & ExtraProps) {
  return <strong>{props.children}</strong>;
}

const descriptionComponents = { strong: Strong } satisfies Components;

/**
 * A playlist's description, editor Markdown with its bold label in front, on
 * the mobile and the desktop page alike.
 */
export function PlaylistDescription({
  description,
  label,
}: {
  description: string;
  label: string | null;
}): React.ReactNode {
  // Streamdown, not react-markdown: it is the app's only markdown engine (see
  // ContentMarkdown) and renders the same three inline elements.
  // `parseIncompleteMarkdown` is off because a playlist description is a
  // finished string, never a stream — left on, a lone `*` in the text would be
  // auto-closed into emphasis.
  return (
    <Streamdown
      allowedElements={["p", "strong", "em"]}
      unwrapDisallowed
      parseIncompleteMarkdown={false}
      components={descriptionComponents}
    >
      {label !== null
        ? `**${label.replace(/\*/g, String.raw`\*`)}:** ${description}`
        : description}
    </Streamdown>
  );
}

export function getPlaylistRoomLogoUrl(
  room: NonNullable<ApiPlaylistModel["room"]>
): string {
  if (room.icoModel !== null) {
    return room.icoModel.sizes !== null
      ? room.icoModel.sizes.icon.file
      : room.icoModel.url;
  }
  return room.organisationModel.logo.url;
}

export default function PlaylistPageHeader({
  playlist,
  articleCount,
  t,
  desktopActions,
}: {
  playlist: ApiPlaylistModel;
  articleCount: number;
  t: (key: string) => string;
  desktopActions: React.ReactNode;
}): React.ReactNode {
  const { activeLang } = useActiveLang();
  const imageUrl = playlist.images[0]?.url ?? null;
  const room = playlist.room;
  const logoUrl =
    room == null
      ? (playlist.company?.logo?.url ?? null)
      : getPlaylistRoomLogoUrl(room);
  const organisationName =
    room?.organisationModel.name ?? playlist.company?.name ?? null;
  const displayName =
    room !== null
      ? getRoomName(room, activeLang)
      : (playlist.company?.name ?? playlist.user?.fullname ?? null);

  return (
    <div className="flex flex-col gap-1">
      {/* Blurred image header — sits behind the transparent ContentHeader, so its
          height must match the header (px-3.5 py-3 → 48px) + the safe-area inset,
          otherwise the blurred band leaks below the header. */}
      <div
        className="relative w-full overflow-hidden"
        style={{ height: "calc(48px + var(--safe-area-top))" }}
      >
        {imageUrl ? (
          <>
            <img
              src={imageUrl}
              alt=""
              className="absolute size-full object-cover"
            />
            <div className="absolute inset-0 backdrop-blur-[10px]" />
          </>
        ) : (
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(144.76deg, #ffffff 0%, #ecf0f5 70.37%, #fcc408 139.87%)",
            }}
          />
        )}
      </div>

      {/* Playlist info */}
      <div className="flex flex-col gap-3 px-5 py-2">
        <div className="flex flex-col gap-2 pt-1">
          {/* Header title / subtitle */}
          {playlist.headerTitle != null && playlist.headerTitle !== "" && (
            <p className="m-0 text-[12px] font-medium leading-[18px] text-black/50">
              {playlist.headerTitle}
            </p>
          )}

          {/* Title */}
          <h1 className="m-0 text-[24px] font-bold leading-[1.4] text-[#303030]">
            {playlist.title}
          </h1>

          {/* Description */}
          {playlist.description != null && playlist.description !== "" && (
            <div className="text-[14px] leading-normal text-[#8A8A8A] [&_p]:m-0">
              <PlaylistDescription
                description={playlist.description}
                label={playlist.descriptionLabel}
              />
            </div>
          )}

          {/* AI Summary */}
          {playlist.summary != null && playlist.summary !== "" && (
            <div
              className="rounded-ds-button-md p-[16px] text-lime-text-2 font-['DM_Sans',sans-serif] text-[14px] leading-[140%]"
              style={{ backgroundColor: "#FEFFF0" }}
            >
              <span className="inline-flex items-center gap-[4px] font-medium text-sm mb-2">
                <StarsIcon />
                {playlist.summarySource === "ai"
                  ? t("AI Summary")
                  : t("Summary")}
              </span>
              <div className="italic text-base text-lime-text-2 whitespace-pre-line">
                {playlist.summary}
              </div>
            </div>
          )}

          {/* Company/room info */}
          {(logoUrl !== null || displayName !== null) && (
            <div className="flex items-center gap-2">
              {logoUrl !== null && (
                <img
                  src={logoUrl}
                  alt=""
                  className="size-[30px] rounded-full object-contain"
                />
              )}
              {displayName !== null && (
                <div className="flex flex-col">
                  <span className="truncate   text-[10px] font-semibold leading-none tracking-[-0.1px] text-primary-black">
                    {displayName}
                  </span>
                  {organisationName !== null &&
                    displayName !== organisationName && (
                      <span className="truncate   text-[7px] font-normal leading-[1.4] text-primary-black">
                        {t("by")} {organisationName}
                      </span>
                    )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Article count + desktop actions */}
        <div className="flex items-center justify-between gap-2">
          {articleCount > 0 ? (
            <div className="flex items-center gap-1">
              <ArticleLabelIcon className="size-4 text-black/40" />
              <span className="text-[12px] font-medium leading-tight text-black/40">
                {articleCount} {t("articles")}
              </span>
            </div>
          ) : (
            <div />
          )}
          {desktopActions}
        </div>
      </div>
    </div>
  );
}
